import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslationService } from '../../../../core/services/translation/translation.service';
import { ClickOutsideDirective } from '../../../directives/click-outside/click-outside.directive';
import { TranslatePipe } from '../../../pipes/translate/translate.pipe';
import { IcArchivedListComponent } from '../../Icons/ic-archived-list/ic-archived-list';
import { IcAttachmentComponent } from '../../Icons/ic-attachment/ic-attachment';
import { IcBatchesComponent } from '../../Icons/ic-batches/ic-batches.component';
import { BookmarkIcon } from '../../Icons/ic-bookmark/ic-bookmark';
import { IcBrokerSolidComponent } from '../../Icons/ic-broker-solid/ic-broker-solid';
import { IcClientSolidComponent } from '../../Icons/ic-client-solid/ic-client-solid';
import { IcDeleteComponent } from '../../Icons/ic-delete/ic-delete.component';
import { IcDocComponent } from '../../Icons/ic-doc/ic-doc.component';
import { IcEmailComponent } from '../../Icons/ic-email/ic-email';
import { IcExportComponent } from '../../Icons/ic-export/ic-export';
import { IcGraphComponent } from '../../Icons/ic-graph/ic-graph.component';
import { IcGridComponent } from '../../Icons/ic-grid/ic-grid';
import { IcImportComponent } from '../../Icons/ic-import/ic-import';
import { IcInfoComponent } from '../../Icons/ic-info/ic-info';
import { IcListComponent } from '../../Icons/ic-list/ic-list.component';
import { IcMovementsComponent } from '../../Icons/ic-movements/ic-movements';
import { IcNotificationComponent } from '../../Icons/ic-notification/ic-notification';
import { IcParcelBoxComponent } from '../../Icons/ic-parcelbox/ic-parcelbox';
import { IcParcelComponent } from '../../Icons/ic-parcel/ic-parcel';
import { IcPartiesSolidComponent } from '../../Icons/ic-parties-solid/ic-parties-solid';
import { IcPlaneComponent } from '../../Icons/ic-plane/ic-plane';
import { IcSettingsComponent } from '../../Icons/ic-settings/ic-settings';
import { IcTasksComponent } from '../../Icons/ic-tasks/ic-tasks.component';
import { IcTransitArrivalComponent } from '../../Icons/ic-transit-arrival/ic-transit-arrival';
import { IcTransitDepartureComponent } from '../../Icons/ic-transit-departure/ic-transit-departure';
import { IcTransitComponent } from '../../Icons/ic-transit/ic-transit';
import { IcUserRoleSolidComponent } from '../../Icons/ic-user-role-solid/ic-user-role-solid';
import { IcUserComponent } from '../../Icons/ic-user/ic-user';
import { IcViewComponent } from '../../Icons/ic-view/ic-view.component';
import { IcWalletComponent } from '../../Icons/ic-wallet/ic-wallet';
import { IcWarningComponent } from '../../Icons/ic-warning/ic-warning';
import { IcDeclarationTemplate } from '../../Icons/settings-icon/ic-declaration-template/ic-declaration-template';
import { IcManiestTemplate } from '../../Icons/settings-icon/ic-manifest-template/ic-manifest-template';
import { IcWarehouseComponent } from '../../Icons/ic-warehouse/ic-warehouse';
import { IcEmcsComponent } from '../../Icons/ic-emcs/ic-emcs';
import { IcCompassComponent } from '../../Icons/ic-compass/ic-compass';

import { TabItem } from '../../../../configs/navigation.config';
export { TabItem };

/**
 * FooterTabComponent component.
 * Provides UI presentation and event handling for footer tab component.
 */
@Component({
  selector: 'app-footer-tab',
  standalone: true,
  imports: [RouterModule, TranslatePipe, CommonModule, ClickOutsideDirective],
  templateUrl: './footer-tab.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./footer-tab.component.scss'],
})
export class FooterTabComponent implements AfterViewInit, OnChanges, OnDestroy {
  /** Property active menu. */
  activeMenu = 'import';
  /** Property active sub menu tab id. */
  activeSubMenuTabId = signal<string | null>(null);

  /** Property icon components. */
  iconComponents: Record<
    string,
    {
      component: import('@angular/core').Type<any>;
      width: number;
      height: number;
    }
  > = {
    list: { component: IcListComponent, width: 12, height: 12 },
    delete: { component: IcDeleteComponent, width: 20, height: 20 },
    bookmark: { component: BookmarkIcon, width: 16, height: 16 },
    warning: { component: IcWarningComponent, width: 14, height: 14 },
    archived: { component: IcArchivedListComponent, width: 14, height: 14 },
    graph: { component: IcGraphComponent, width: 14, height: 14 },
    grid: { component: IcGridComponent, width: 14, height: 14 },
    box: { component: IcParcelBoxComponent, width: 14, height: 14 },
    parcel: { component: IcParcelComponent, width: 13, height: 15 },
    broker: { component: IcBrokerSolidComponent, width: 14, height: 14 },
    client: { component: IcClientSolidComponent, width: 14, height: 14 },
    parties: { component: IcPartiesSolidComponent, width: 14, height: 14 },
    people: { component: IcPartiesSolidComponent, width: 14, height: 14 },
    role: { component: IcUserRoleSolidComponent, width: 14, height: 14 },
    user: { component: IcUserComponent, width: 14, height: 14 },
    'declaration-template': {
      component: IcDeclarationTemplate,
      width: 14,
      height: 14,
    },
    'manifest-template': {
      component: IcManiestTemplate,
      width: 14,
      height: 14,
    },
    attachment: { component: IcAttachmentComponent, width: 18, height: 18 },
    movements: { component: IcMovementsComponent, width: 14, height: 14 },
    tasks: { component: IcTasksComponent, width: 12, height: 12 },
    info: { component: IcInfoComponent, width: 4, height: 10 },
    batches: { component: IcBatchesComponent, width: 12, height: 15 },
    config: { component: IcSettingsComponent, width: 14, height: 14 },
    users: { component: IcUserComponent, width: 14, height: 14 },
    shipment: { component: IcPlaneComponent, width: 14, height: 10 },
    mawb: { component: IcPlaneComponent, width: 16, height: 12 },
    import: { component: IcImportComponent, width: 14, height: 14 },
    'import-shipment': { component: IcImportComponent, width: 14, height: 14 },
    'export-shipment': { component: IcExportComponent, width: 14, height: 15 },
    declaration: { component: IcParcelBoxComponent, width: 14, height: 14 },
    'import-declaration': {
      component: IcImportComponent,
      width: 14,
      height: 14,
    },
    'export-declaration': {
      component: IcExportComponent,
      width: 14,
      height: 15,
    },
    wallet: { component: IcWalletComponent, width: 14, height: 14 },
    truck: { component: IcTransitComponent, width: 14, height: 14 },
    'ic-transit': { component: IcTransitComponent, width: 14, height: 14 },
    'transit-arrival-icon': {
      component: IcTransitArrivalComponent,
      width: 13,
      height: 12,
    },
    'transit-departure-icon': {
      component: IcTransitDepartureComponent,
      width: 13,
      height: 12,
    },
    notification: { component: IcNotificationComponent, width: 14, height: 14 },
    view: { component: IcViewComponent, width: 14, height: 14 },
    doc: { component: IcDocComponent, width: 14, height: 16 },
    email: { component: IcEmailComponent, width: 14, height: 10 },
    warehouse: { component: IcWarehouseComponent, width: 14, height: 14 },
    emcs: { component: IcEmcsComponent, width: 14, height: 14 },
    compass: { component: IcCompassComponent, width: 14, height: 14 },
  };

  /**
   * Input property for tabs.
   */
  @Input() tabs: TabItem[] = [];
  /**
   * Input property for active tab.
   */
  @Input() activeTab!: string;
  /**
   * Input property for highlight background.
   */
  @Input() highlightBackground = false;

  /**
   * Event emitter for tab change changes.
   */
  @Output() tabChange = new EventEmitter<string>();

  /**
   * Reference to the tab container element or component.
   */
  @ViewChild('tabContainer', { static: false })
  tabContainer?: ElementRef<HTMLDivElement>;
  /**
   * Reference to the scroll container element or component.
   */
  @ViewChild('scrollContainer', { static: false })
  scrollContainer?: ElementRef<HTMLDivElement>;

  // 9-slice scaling state (Straight diagonal with very large, gentle top curve)
  /** Property original width. */
  private readonly ORIGINAL_WIDTH = 300;
  /** Property original height. */
  private readonly ORIGINAL_HEIGHT = 48;
  /** Inner fill path. */
  private readonly ORIGINAL_PATH =
    'M 0 48 L 48 24 Q 96 0 130 0 H 170 Q 204 0 252 24 L 300 48 Z';
  /** Open path for top/left/right stroke (no bottom edge). */
  private readonly ORIGINAL_STROKE_PATH =
    'M 0 48 L 48 24 Q 96 0 130 0 H 170 Q 204 0 252 24 L 300 48';

  /** Property dynamic path. */
  readonly dynamicPath = signal('');
  /** Property dynamic stroke path. */
  readonly dynamicStrokePath = signal('');
  /** Property current width. */
  readonly currentWidth = signal(300);
  /** Property current height. */
  readonly currentHeight = signal(48);
  /** Property resize observer. */
  private resizeObserver: ResizeObserver | null = null;

  // Scroll state
  /** Reactive signal holding scroll percentage. */
  scrollPercentage = signal(0);
  /** Reactive signal holding show navigation. */
  showNavigation = signal(false);
  /** Reactive signal holding can go previous. */
  canGoPrevious = signal(false);
  /** Reactive signal holding can go next. */
  canGoNext = signal(false);

  constructor(
    private ngZone: NgZone,
    private translationService: TranslationService
  ) {
    this.dynamicPath.set(this.ORIGINAL_PATH);
    this.dynamicStrokePath.set(this.ORIGINAL_STROKE_PATH);
  }

  /**
   * Lifecycle hook that runs after component view initialization.
   */
  ngAfterViewInit() {
    this.setupResizeObserver();
    setTimeout(() => {
      this.calculateMaxTabs();
      this.updateScrollState();
      this.scrollActiveTabIntoView();
    }, 100);
  }

  /**
   * Lifecycle hook that cleans up subscriptions on component destruction.
   */
  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  /**
   * Handles setup resize observer logic.
   */
  private setupResizeObserver() {
    if (typeof ResizeObserver !== 'undefined' && this.tabContainer) {
      this.resizeObserver = new ResizeObserver((entries) => {
        this.ngZone.run(() => {
          const rect = entries[0].target.getBoundingClientRect();
          if (rect.width > 0) {
            this.currentWidth.set(rect.width);
            this.currentHeight.set(rect.height);
            this.updateDynamicPath();
            this.calculateMaxTabs();
            this.updateScrollState();
            this.scrollActiveTabIntoView();
          }
        });
      });
      this.resizeObserver.observe(this.tabContainer.nativeElement);
    }
  }

  /**
   * Handles update dynamic path logic.
   */
  private updateDynamicPath() {
    this.dynamicPath.set(this.scalePath(this.ORIGINAL_PATH));
    this.dynamicStrokePath.set(this.scalePath(this.ORIGINAL_STROKE_PATH));
  }

  /**
   * Handles scale path logic.
   * @param path - The path
   */
  private scalePath(path: string): string {
    const targetWidth = this.currentWidth();
    const targetHeight = this.currentHeight();
    const shiftX = targetWidth - this.ORIGINAL_WIDTH;
    const shiftY = targetHeight - this.ORIGINAL_HEIGHT;

    const thresholdX = 150;
    const thresholdY = 24;

    const commands = path.match(/[MLHVCSQTAZ][^MLHVCSQTAZ]*/gi) || [];

    const updated = commands.map((command) => {
      const cmd = command[0];
      const rest = command.slice(1).trim();
      if (!rest) return command;

      const nums = rest
        .split(/[\s,]+/)
        .filter((s) => s !== '')
        .map((n) => Number(n));

      const transformed = nums.map((value, i) => {
        const upperCmd = cmd.toUpperCase();
        let isX = false;
        let isY = false;

        switch (upperCmd) {
          case 'M':
          case 'L':
          case 'T':
            isX = i % 2 === 0;
            isY = i % 2 === 1;
            break;
          case 'C':
            isX = i % 2 === 0;
            isY = i % 2 === 1;
            break;
          case 'Q':
          case 'S':
            isX = i % 2 === 0;
            isY = i % 2 === 1;
            break;
          case 'H':
            isX = true;
            break;
          case 'V':
            isY = true;
            break;
          case 'A':
            isX = i === 5;
            isY = i === 6;
            break;
        }

        if (isX && value > thresholdX) {
          return +(value + shiftX).toFixed(4);
        }

        if (isY && value > thresholdY) {
          return +(value + shiftY).toFixed(4);
        }

        return value;
      });

      return `${cmd}${transformed.join(' ')}`;
    });

    return updated.join('');
  }

  /**
   * Handles get view box logic.
   */
  getViewBox(): string {
    return `0 0 ${this.currentWidth()} ${this.currentHeight()}`;
  }

  /**
   * Lifecycle hook that responds when data-bound properties change.
   * @param changes - The changed properties map
   */
  ngOnChanges(changes: SimpleChanges) {
    setTimeout(() => {
      this.calculateMaxTabs();
      this.updateScrollState();
      if (changes['activeTab'] || changes['tabs']) {
        this.scrollActiveTabIntoView();
      }
    }, 100);
  }

  /**
   * Handles calculate max tabs logic.
   */
  private calculateMaxTabs() {
    if (!this.scrollContainer) return;
    const el = this.scrollContainer.nativeElement;
    this.showNavigation.set(el.scrollWidth > el.clientWidth);
  }

  /**
   * Handles handle scroll logic.
   */
  handleScroll() {
    this.updateScrollState();
  }

  /**
   * Handles update scroll state logic.
   */
  updateScrollState() {
    if (!this.scrollContainer) return;
    const el = this.scrollContainer.nativeElement;
    const scrollLeft = el.scrollLeft;
    const maxScroll = el.scrollWidth - el.clientWidth;

    if (maxScroll > 0) {
      this.scrollPercentage.set((scrollLeft / maxScroll) * 100);
      this.canGoPrevious.set(scrollLeft > 5);
      this.canGoNext.set(scrollLeft < maxScroll - 5);
    } else {
      this.scrollPercentage.set(0);
      this.canGoPrevious.set(false);
      this.canGoNext.set(false);
    }
  }

  /**
   * Handles on tab click logic.
   * @param event - The event
   * @param tab - The tab
   */
  onTabClick(event: Event | MouseEvent, tab: TabItem) {
    if (tab.children && tab.children.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      this.activeSubMenuTabId.set(
        this.activeSubMenuTabId() === tab.id ? null : tab.id
      );
    } else {
      this.activeSubMenuTabId.set(null);
      this.selectTab(tab.id);
    }
  }

  /**
   * Handles close sub menu logic.
   */
  closeSubMenu() {
    this.activeSubMenuTabId.set(null);
  }

  /**
   * Handles select tab logic.
   * @param tabId - The tab id
   */
  selectTab(tabId: string) {
    this.tabChange.emit(tabId);
  }

  /**
   * Handles is active logic.
   * @param tabId - The tab id
   */
  isActive(tabId: string): boolean {
    return this.activeTab === tabId;
  }

  /**
   * Handles go to previous logic.
   */
  goToPrevious() {
    if (this.scrollContainer) {
      this.scrollContainer.nativeElement.scrollBy({
        left: -150,
        behavior: 'smooth',
      });
    }
  }

  /**
   * Handles go to next logic.
   */
  goToNext() {
    if (this.scrollContainer) {
      this.scrollContainer.nativeElement.scrollBy({
        left: 150,
        behavior: 'smooth',
      });
    }
  }

  /** Keeps the active tab visible inside the horizontal scroll area. */
  private scrollActiveTabIntoView(): void {
    if (!this.scrollContainer?.nativeElement || !this.activeTab) {
      return;
    }

    const container = this.scrollContainer.nativeElement;
    const activeEl = container.querySelector(
      `[data-tab-id="${CSS.escape(this.activeTab)}"]`
    ) as HTMLElement | null;

    if (!activeEl) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const activeRect = activeEl.getBoundingClientRect();
    const edgePadding = 12;

    if (activeRect.left < containerRect.left + edgePadding) {
      container.scrollTo({
        left:
          container.scrollLeft +
          (activeRect.left - containerRect.left) -
          edgePadding,
        behavior: 'smooth',
      });
      return;
    }

    if (activeRect.right > containerRect.right - edgePadding) {
      container.scrollTo({
        left:
          container.scrollLeft +
          (activeRect.right - containerRect.right) +
          edgePadding,
        behavior: 'smooth',
      });
    }
  }
}
